import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import listRegistryAssets, { RegistryAsset } from './api/registry/listRegistryAssets'

type UseRegistryAssetsParams = {
  search?: string
  kind?: string
  pageSize?: number
}

export type UseRegistryAssetsResult = {
  assets: RegistryAsset[]
  loadMore: () => void
  reload: () => void
  hasMore: boolean
  loading: boolean
  error: Error | null
}

const DEFAULT_PAGE_SIZE = 50

const useRegistryAssets = ({
  search,
  kind,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseRegistryAssetsParams = {}): UseRegistryAssetsResult => {
  const query = useInfiniteQuery({
    queryKey: ['registry-assets', kind ?? '', search ?? ''],
    queryFn: async ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) =>
      listRegistryAssets({ cursor: pageParam, pageSize, kind, search, signal }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined,
  })

  const assets = useMemo(
    () => query.data?.pages.flatMap((page) => page.documents) ?? [],
    [query.data?.pages, query.dataUpdatedAt]
  )

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage()
  }, [query])

  const reload = useCallback(() => {
    query.refetch()
  }, [query])

  return {
    assets,
    loadMore,
    reload,
    hasMore: query.hasNextPage ?? false,
    loading: query.isLoading || query.isFetchingNextPage || query.isRefetching,
    error: query.error as Error | null,
  }
}

export default useRegistryAssets
